import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { breakpoints } from '../assets/styles/mediaBreakpoints'
import { useParams, NavLink } from 'react-router-dom'
import heroesData from '../assets/data/superheroes.json'
import { Hero } from '../types/hero'

type Category =
  | 'ALL'
  | 'PLANET'
  | 'PROSPERITY'
  | 'PARTNERSHIPS'
  | 'PEACE'
  | 'PEOPLE'

const QuizRelevantPage = () => {
  const { id } = useParams()
  const [heroes, setHeroes] = useState<Hero[]>([])
  const [mainHero, setMainHero] = useState<Hero | null>(null)
  const [relatedHeroes, setRelatedHeroes] = useState<Hero[]>([])
  const [activeCategory, setActiveCategory] = useState<Category>('ALL')
  const [isSaved, setIsSaved] = useState(false)

  const categories: Category[] = [
    'ALL',
    'PLANET',
    'PROSPERITY',
    'PARTNERSHIPS',
    'PEACE',
    'PEOPLE',
  ]

  const getHeroPBasket = (heroId: number): Category | null => {
    if ([6, 12, 13, 14, 15].includes(heroId)) {
      return 'PLANET'
    } else if ([7, 8, 9, 11, 12].includes(heroId)) {
      return 'PROSPERITY'
    } else if ([17].includes(heroId)) {
      return 'PARTNERSHIPS'
    } else if ([10, 16].includes(heroId)) {
      return 'PEACE'
    } else if ([1, 2, 3, 4, 5].includes(heroId)) {
      return 'PEOPLE'
    }
    return null
  }

  useEffect(() => {
    const allHeroes = JSON.parse(JSON.stringify(heroesData)) as Hero[]
    setHeroes(allHeroes)

    const foundHero = allHeroes.find((hero) => hero.id.toString() === id)
    if (foundHero) {
      setMainHero(foundHero)

      // Get the P basket of the main hero
      const mainHeroPBasket = getHeroPBasket(foundHero.id)

      // Filter related heroes to only include those from the same P basket
      if (mainHeroPBasket) {
        const filteredRelatedHeroes = allHeroes.filter(
          (hero) =>
            hero.id.toString() !== id &&
            getHeroPBasket(hero.id) === mainHeroPBasket,
        )
        setRelatedHeroes(filteredRelatedHeroes)
      } else {
        // Fallback if no P basket is found
        setRelatedHeroes(allHeroes.filter((hero) => hero.id.toString() !== id))
      }
    }
  }, [id])

  if (!mainHero) {
    return <div>Loading...</div>
  }

  return (
    <PageContainer>
      <HeaderContainer>
        <PageTitle>Choose you superhero</PageTitle>
        <ButtonContainer>
          <SaveButton $saved={isSaved} onClick={() => setIsSaved(true)}>
            {isSaved ? (
              <>
                <span style={{ fontSize: '20px' }}>✓</span> Saved!
              </>
            ) : (
              'Save change'
            )}
          </SaveButton>
          {!isSaved && <CancelButton to="/quiz">Cancel</CancelButton>}
        </ButtonContainer>
      </HeaderContainer>

      <MainSection>
        <MainSectionInner>
          <MainHeroSection>
            <SectionTitle>Superhero relate {mainHero.name}</SectionTitle>
            <SdgNumberText>WHAT&apos;S SDG NUMBER?</SdgNumberText>

            <HeroCardGrid>
              {/* Display the main hero and 3 other random heroes */}
              {[mainHero, ...relatedHeroes.slice(0, 3)].map((hero) => (
                <HeroCard
                  key={hero.id}
                  to={`/superhero/${hero.id}`}
                  $sdg={hero.id.toString()}
                  $isSelected={hero.id.toString() === id}
                >
                  <TextOverlay>
                    <HeroTitle>{hero.name}</HeroTitle>
                    <HeroSubtitle>
                      SDG {hero.id} | {hero.sdg}
                    </HeroSubtitle>
                    <DetailLink>Detail &gt;</DetailLink>
                  </TextOverlay>
                </HeroCard>
              ))}
            </HeroCardGrid>
          </MainHeroSection>

          <AllHeroesSection>
            <SectionTitle>Our all superheroes</SectionTitle>
            <FilterContainer>
              {categories.map((category) => (
                <FilterButton
                  key={category}
                  $active={activeCategory === category}
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </FilterButton>
              ))}
            </FilterContainer>

            <AllHeroesGrid>
              {/* Filter heroes based on selected category */}
              {heroes
                .filter((hero) => {
                  if (activeCategory === 'ALL') return true
                  const heroCategory = getHeroPBasket(hero.id)
                  return heroCategory === activeCategory
                })
                .map((hero) => (
                  <SmallHeroCard
                    key={hero.id}
                    to={`/superhero/${hero.id}`}
                    $sdg={hero.id.toString()}
                    $isSelected={hero.id.toString() === id}
                  >
                    <TextOverlay>
                      <SmallHeroTitle>{hero.name}</SmallHeroTitle>
                      <SmallHeroSubtitle>
                        SDG {hero.id} | {hero.sdg}
                      </SmallHeroSubtitle>
                      <DetailLink>Detail &gt;</DetailLink>
                    </TextOverlay>
                  </SmallHeroCard>
                ))}
            </AllHeroesGrid>
          </AllHeroesSection>
        </MainSectionInner>
      </MainSection>
    </PageContainer>
  )
}

const PageContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  background: repeating-linear-gradient(
    90deg,
    ${(props) => props.theme.colors['primary-100']} 0px,
    ${(props) => props.theme.colors['primary-100']} 40px,
    white 40px,
    white 80px
  );
`

const HeaderContainer = styled.div`
  align-self: stretch;
  height: 52px;
  justify-content: space-between;
  align-items: center;
  display: inline-flex;
  padding: 32px 64px;
  background: var(--Color-White, white);

  @media (max-width: ${breakpoints.desktop}px) {
    padding: 24px;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    height: auto;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    padding: 16px;
    width: 100%;
    align-items: center;
  }
`

const PageTitle = styled.h5`
  color: var(--Color-Grayscale-500, #2a2a2a);
  font-size: 24px;
  font-family: Soleil;
  font-weight: 700;
  line-height: 32px;
  word-wrap: break-word;

  @media (max-width: ${breakpoints.mobile}px) {
    font-size: 20px;
    line-height: 28px;
    text-align: center;
    width: 100%;
  }
`

const ButtonContainer = styled.div`
  flex: 1 1 0;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  display: flex;

  @media (max-width: ${breakpoints.mobile}px) {
    width: 100%;
    justify-content: center;
    flex-direction: column;
    gap: 12px;
  }
`

const SaveButton = styled.button<{ $saved?: boolean }>`
  width: 200px;
  align-self: stretch;
  padding: 14px 24px;
  background: ${(props) =>
    props.$saved ? '#bde039' : 'var(--Color-Grayscale-500, #2A2A2A)'};
  border-radius: 100px;
  border: none;
  justify-content: center;
  align-items: center;
  gap: 10px;
  display: flex;
  color: ${(props) => (props.$saved ? '#2A2A2A' : 'white')};
  font-size: 16px;
  font-family: Soleil;
  font-weight: 700;
  word-wrap: break-word;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${(props) => (props.$saved ? '#bde039' : '#bde039')};
    color: #2a2a2a;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    width: 100%;
    padding: 12px 20px;
    font-size: 14px;
  }
`

const CancelButton = styled(NavLink)`
  height: 52px;
  padding: 14px 24px;
  border-radius: 100px;
  outline: 2px var(--Color-Grayscale-500, #2a2a2a) solid;
  outline-offset: -2px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  display: flex;
  color: var(--Color-Grayscale-500, #2a2a2a);
  font-size: 16px;
  font-family: Soleil;
  font-weight: 700;
  word-wrap: break-word;
  text-decoration: none;

  &:hover {
    background-color: var(--Color-Grayscale-100);
  }

  @media (max-width: ${breakpoints.mobile}px) {
    width: 100%;
    padding: 12px 20px;
    font-size: 14px;
    height: auto;
  }
`

const MainSection = styled.section`
  width: 100%;
  padding-top: 40px;
  padding-bottom: 120px;
  padding-left: 100px;
  padding-right: 100px;

  @media (max-width: ${breakpoints.desktop}px) {
    padding-left: 64px;
    padding-right: 64px;
    padding-bottom: 80px;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    padding-left: 16px;
    padding-right: 16px;
    padding-top: 24px;
    padding-bottom: 48px;
  }
`

const MainSectionInner = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: ${breakpoints.desktop}px) {
    max-width: 100%;
  }
`

const MainHeroSection = styled.section`
  margin: 0 auto;
  width: 100%;
  max-width: 1056px;
  height: auto;
  min-height: 512px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: ${breakpoints.desktop}px) {
    min-height: 450px;
  }
`

const SectionTitle = styled.h2`
  color: var(--Color-Grayscale-500, #2a2a2a);
  font-size: 48px;
  font-family: Soleil;
  font-weight: 700;
  line-height: 56px;
  word-wrap: break-word;
  margin: 0;

  @media (max-width: ${breakpoints.desktop}px) {
    width: 100%;
    font-size: 40px;
    line-height: 48px;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    font-size: 24px;
    line-height: 32px;
    padding: 0;
  }
`

const SdgNumberText = styled.p`
  color: var(--Color-Grayscale-500, #2a2a2a);
  font-size: 16px;
  font-family: Soleil;
  font-weight: 700;
  text-transform: uppercase;
  line-height: 16px;
  letter-spacing: 1.6px;
  word-wrap: break-word;
  margin: 0;

  @media (max-width: ${breakpoints.mobile}px) {
    font-size: 12px;
    line-height: 12px;
    letter-spacing: 1.2px;
  }
`

const FilterButton = styled.button<{ $active?: boolean }>`
  padding: 8px 16px;
  background: ${(props) =>
    props.$active ? 'var(--Color-Grayscale-500, #2A2A2A)' : 'transparent'};
  color: ${(props) =>
    props.$active ? 'white' : 'var(--Color-Grayscale-500, #2A2A2A)'};
  border: 1px solid var(--Color-Grayscale-500, #2a2a2a);
  border-radius: 100px;
  font-family: Soleil;
  font-weight: 700;
  font-size: 16px;
  text-transform: uppercase;
  line-height: 16px;
  letter-spacing: 1.6px;
  word-wrap: break-word;
  cursor: pointer;
  transition: all 0.3s ease;

  @media (max-width: ${breakpoints.mobile}px) {
    padding: 6px 12px;
    font-size: 12px;
    line-height: 12px;
    letter-spacing: 1.2px;
  }
`

const FilterContainer = styled.div`
  display: flex;
  gap: 8px;
  margin: 24px 0;
  flex-wrap: wrap;

  @media (max-width: ${breakpoints.mobile}px) {
    gap: 6px;
    margin: 16px 0;
    justify-content: center;
  }
`

const AllHeroesSection = styled.section`
  margin-top: 64px;
  display: flex;
  flex-direction: column;

  @media (max-width: ${breakpoints.mobile}px) {
    margin-top: 40px;
  }
`

const AllHeroesGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 24px;
  margin-top: 32px;

  @media (max-width: ${breakpoints.desktop}px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    grid-template-columns: 1fr;
  }
`

const HeroCardGrid = styled.div`
  width: 100%;
  max-width: 1056px;
  height: auto;
  min-height: 360px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  justify-content: center;

  @media (max-width: ${breakpoints.desktop}px) {
    width: 100%;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`

const HeroCard = styled(NavLink)<{ $sdg: string; $isSelected: boolean }>`
  width: 100%;
  height: 360px;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  text-decoration: none;
  background: url(/images/quiz/SDG_${(props) => props.$sdg}.png) center/cover
    no-repeat;

  @media (max-width: ${breakpoints.desktop}px) {
    height: 320px;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    height: 400px;
    width: 90%;
    margin: 0 auto;
    background-position-x: center;
    background-position-y: -30px;
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 50%;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.9) 100%
    );
    z-index: 1;
  }

  ${(props) =>
    props.$isSelected &&
    `
    border: 4px solid ${props.theme.colors[`sdg${props.$sdg}-500` as keyof typeof props.theme.colors]};
  `}
`

const SmallHeroCard = styled(NavLink)<{ $sdg: string; $isSelected: boolean }>`
  width: 100%;
  height: 270px;
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  text-decoration: none;
  background: url(/images/quiz/SDG_${(props) => props.$sdg}.png) center/cover
    no-repeat;

  @media (max-width: ${breakpoints.desktop}px) {
    height: 240px;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    height: 400px;
    width: 90%;
    margin: 0 auto;
    background-position-x: center;
    background-position-y: -30px;
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 50%;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.9) 100%
    );
    z-index: 1;
  }

  ${(props) =>
    props.$isSelected &&
    `
    border: 4px solid ${props.theme.colors[`sdg${props.$sdg}-500` as keyof typeof props.theme.colors]};
  `}
`

const HeroTitle = styled.h5`
  color: var(--Color-White, white);
  font-size: 20px;
  font-family: Soleil;
  font-weight: 700;
  line-height: 28px;
  word-wrap: break-word;
  margin: 0;
  z-index: 2;
`
const SmallHeroTitle = styled.h5`
  color: var(--Color-White, white);
  font-size: 14px;
  font-family: Soleil;
  font-weight: 600;
  line-height: 18px;
  word-wrap: break-word;
  margin: 0;
  z-index: 2;
`

const TextOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  z-index: 2;
`

const HeroSubtitle = styled.p`
  color: var(--Color-White, white);
  font-size: 12px;
  font-family: Soleil;
  font-weight: 600;
  line-height: 16px;
  word-wrap: break-word;
  opacity: 0.8;
  margin: 0;
  position: relative;
  z-index: 2;
`
const SmallHeroSubtitle = styled.p`
  color: var(--Color-White, white);
  font-size: 11px;
  font-family: Soleil;
  font-weight: 600;
  line-height: 14px;
  word-wrap: break-word;
  opacity: 0.8;
  margin: 0;
  position: relative;
  z-index: 2;
`

const DetailLink = styled.div`
  display: flex;
  align-items: center;
  font-family: Soleil;
  color: var(--Color-White, white);
  font-size: 10px;
  font-weight: 600;
  word-wrap: break-word;
  margin-top: 2px;
`

export default QuizRelevantPage
